/**
 * PieceAmplificationsDoc — every amplification for this piece, in the main
 * window (FEAT-092 / CCM-64).
 *
 * The side column's "Amplifications" section used to render AmplificationsPanel
 * inline, in a 344px strip — cramped, the founder's own word. This document
 * follows the same pattern PieceSummaryDoc and the Voice assessment already
 * established: the detail moves to the main window, the side column keeps a
 * button.
 *
 * Non-posted rows (draft/approved/scheduled/failed) get the full
 * SocialPostEditor — the same fields and actions Social Calendar's own drawer
 * offers, sharing its validation rather than re-deriving it (ADR-022). Posted
 * rows are history, not work, and get a single quiet summary line instead —
 * the founder's own distinction.
 *
 * Offspring (a piece's child pieces) is a different concept — a real
 * independent piece, not a social post — and stays in its own side-column
 * section (OffspringPanel), not here.
 */
import { useState } from "react";
import { isEligibleAsPrimary } from "@shared/publish-url";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi, useApiQuery } from "@/lib/api";
import { ageString } from "@/lib/format";
import { amplifierDisplayName, useAmplifierLookup } from "@/lib/amplifier-lookup";
import { useLinkedinArticleLabel } from "@/lib/linkedin-article-label";
import {
  socialAccountDisplayName,
  useSocialAccountsForWorkspace,
  type SocialAccountLookupRow,
} from "@/lib/social-account-lookup";
import { SocialPostEditor } from "./SocialPostEditor";
import { Icon } from "./Icon";
import { defaultScheduledAtLocal, scheduledAtTooSoon } from "@/lib/schedule-validation";
import type { PieceAmplificationPost } from "@/types/piece";
import { useRole } from "@/lib/useRole";

/** FEAT-080 / S-351 — only the fields the link picker needs. */
interface PublishResultLite {
  id: number;
  target_name: string;
  external_url: string | null;
  status: string;
  site_url: string | null;
  is_primary: boolean;
}

export interface PieceAmplificationsDocProps {
  pieceId: number;
  agencyclientId: number | null;
}

const PLATFORM_LABEL: Record<string, string> = {
  linkedin: "LinkedIn",
  // FEAT-086 / S-388 — goes out with the article through LinkedIn's own
  // "Create post modal", never through Zernio. Labelled apart because the two
  // are indistinguishable otherwise and behave nothing alike.
  linkedin_article: "LinkedIn article post",
  x: "X",
  instagram: "Instagram",
  threads: "Threads",
};

export function PieceAmplificationsDoc({ pieceId, agencyclientId }: PieceAmplificationsDocProps) {
  const qc = useQueryClient();
  const { data: postsData, isLoading } = useApiQuery<{ data: PieceAmplificationPost[] }>(
    ["piece-amplification-posts", pieceId],
    `/social-calendar?piece_id=${pieceId}`
  );
  const accounts = useSocialAccountsForWorkspace(agencyclientId);
  const accountsById = new Map(accounts.map((a) => [a.id, a]));
  const posts = postsData?.data ?? [];
  const nonPosted = posts.filter((p) => p.status !== "posted");
  const posted = posts.filter((p) => p.status === "posted");

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["piece-amplification-posts", pieceId] });
  };

  const [openVariant, setOpenVariant] = useState<"reamplify" | "linkedin_intro" | null>(null);
  // FEAT-108 / S-438 — generating posts is Admin and Editor work.
  const { canEdit } = useRole();

  return (
    <div className="pamp-doc">
      {isLoading && <div className="panel-empty">Loading…</div>}

      {!isLoading && posts.length === 0 && (
        <p className="pside-empty">No amplifications yet for this piece.</p>
      )}

      {nonPosted.length > 0 && (
        <div className="pamp-doc-section">
          {nonPosted.map((post) => (
            <div key={post.id} className="pamp-doc-post">
              <PostHeader post={post} accountsById={accountsById} />
              <SocialPostEditor row={post} showMarkPosted onChanged={invalidate} onRemoved={invalidate} />
            </div>
          ))}
        </div>
      )}

      {posted.length > 0 && (
        <div className="pamp-doc-section">
          <p className="panel-section-label">Posted</p>
          <ul className="report-list">
            {posted.map((post) => (
              <PostedSummaryRow key={post.id} post={post} accountsById={accountsById} />
            ))}
          </ul>
        </div>
      )}

      {canEdit && (
      <div className="pamp-doc-section">
        <p className="panel-section-label">Create More</p>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            className="btn-dec btn-review"
            onClick={() => setOpenVariant((v) => (v === "reamplify" ? null : "reamplify"))}
          >
            {openVariant === "reamplify" ? "Cancel" : "Generate another amplification"}
          </button>
          <button
            className="btn-dec btn-review"
            onClick={() => setOpenVariant((v) => (v === "linkedin_intro" ? null : "linkedin_intro"))}
          >
            {openVariant === "linkedin_intro" ? "Cancel" : "Create LinkedIn intro post"}
          </button>
        </div>
        {openVariant && (
          <AccountPickerForm
            pieceId={pieceId}
            accounts={accounts}
            variant={openVariant}
            onGenerated={invalidate}
            onDone={() => setOpenVariant(null)}
          />
        )}
      </div>
      )}
    </div>
  );
}

/**
 * Review fix — `PostHeader` and `PostedSummaryRow` both need a post's actor
 * label; this was duplicated between them until extracted here. (The same
 * shape also exists in `SocialCalendarView.tsx`'s `PostCard`/`PostDrawer`,
 * pre-dating this file — left alone since it's outside this change's scope.)
 */
function useActorLabel(
  post: PieceAmplificationPost,
  accountsById: Map<number, SocialAccountLookupRow>
): string {
  const amp = useAmplifierLookup(post.account_id == null ? post.amplifier_id : null);
  // A LinkedIn article post is published by its newsletter's publish target,
  // never by the account its row happens to name.
  const articleLabel = useLinkedinArticleLabel(post.platform, post.piece_id);
  if (articleLabel) return articleLabel;
  return post.account_id != null
    ? socialAccountDisplayName(accountsById.get(post.account_id), post.account_id)
    : amplifierDisplayName(amp, post.amplifier_id ?? 0);
}

function PostHeader({
  post,
  accountsById,
}: {
  post: PieceAmplificationPost;
  accountsById: Map<number, SocialAccountLookupRow>;
}) {
  const actorLabel = useActorLabel(post, accountsById);
  return (
    <div className="pamp-doc-post-header">
      <span className="pamp-doc-post-meta">
        {PLATFORM_LABEL[post.platform] ?? post.platform} · {actorLabel}
      </span>
      {post.is_linkedin_intro && <span className="pill pill-info">LinkedIn Intro</span>}
    </div>
  );
}

/**
 * Posted amplifications are history, not work — the founder's own
 * distinction. One line, no editor, no action buttons.
 */
function PostedSummaryRow({
  post,
  accountsById,
}: {
  post: PieceAmplificationPost;
  accountsById: Map<number, SocialAccountLookupRow>;
}) {
  const actorLabel = useActorLabel(post, accountsById);
  return (
    <li className="report-row">
      <span className="report-row-icon">
        <Icon name="social" size={18} />
      </span>
      <span className="report-row-body">
        <span className="report-row-title">
          {PLATFORM_LABEL[post.platform] ?? post.platform} · {actorLabel}
        </span>
        <span className="report-row-sub">
          {ageString(post.created_at)}
          {post.published_url && (
            <>
              {" — "}
              <a href={post.published_url} target="_blank" rel="noopener noreferrer" className="settings-link">
                View published post ↗
              </a>
            </>
          )}
        </span>
      </span>
    </li>
  );
}

// FEAT-063 (S-318) / FEAT-064 (S-320) — account-picker form, relocated
// verbatim from AmplificationsPanel.tsx (checkbox per connected account,
// optional angle, optional link pick). Only ever
// produces a social_calendar row, never a piece — Offspring is a different
// form entirely and stays with OffspringPanel.
function AccountPickerForm({
  pieceId,
  accounts: allAccounts,
  variant,
  onGenerated,
  onDone,
}: {
  pieceId: number;
  accounts: SocialAccountLookupRow[];
  variant: "reamplify" | "linkedin_intro";
  /** Called on every successful generation, before the form closes. */
  onGenerated: () => void;
  onDone: () => void;
}) {
  const api = useApi();
  const isLinkedinIntro = variant === "linkedin_intro";
  const accounts = isLinkedinIntro ? allAccounts.filter((a) => a.platform === "linkedin") : allAccounts;
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [angle, setAngle] = useState("");
  // FEAT-091 — optional, defaults to now + 30 minutes. Setting it does not
  // approve the row (the route inserts every generated post as draft
  // regardless); clearing it back to "" leaves scheduled_at unset, today's
  // behavior. Plain onChange, no self-correction: SocialPostEditor snaps a
  // too-soon pick forward because its value is about to hit /schedule,
  // which hard-rejects the past. A creation-time value lands on a draft
  // row nothing dispatches, so rewriting the operator's choice here would
  // be a surprise with no rule behind it — the hint below warns instead.
  const [scheduledAt, setScheduledAt] = useState(() => defaultScheduledAtLocal());

  // FEAT-080 / S-351 — which of the piece's live URLs these posts should point
  // at. Only a real question when the piece has more than one eligible link; a
  // single-target piece must never see this prompt, per the spec.
  const resultsQ = useApiQuery<{ data: PublishResultLite[] }>(
    ["piece-publish-results", pieceId],
    `/pieces/${pieceId}/publish-results`
  );
  const eligible = (resultsQ.data?.data ?? []).filter((r) =>
    isEligibleAsPrimary(r.status, r.external_url, r.site_url)
  );
  // Not asked for a LinkedIn intro post: that variant generates only the
  // linkedin_article note, which carries no URL slot at all by design
  // (FEAT-086 / S-387 — the article is attached to the post). Forcing a choice
  // that cannot affect the output is a question with no answer.
  const mustPickLink = !isLinkedinIntro && eligible.length > 1;
  const [publishResultId, setPublishResultId] = useState<number | null>(null);
  // Default to the piece's current primary — the link the automatic path would
  // have used, so the pre-selected answer is the one that is already true.
  const chosenResultId = publishResultId ?? eligible.find((r) => r.is_primary)?.id ?? null;

  const toggle = (id: number) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const generate = useMutation({
    mutationFn: (): Promise<{ created: unknown[] }> => {
      if (selectedIds.length === 0) throw new Error("Select at least one account.");
      if (mustPickLink && chosenResultId == null) throw new Error("Pick which link these posts should reference.");
      return api(`/pieces/${pieceId}/amplify`, {
        method: "POST",
        body: JSON.stringify({
          account_ids: selectedIds,
          angle: angle.trim() || undefined,
          variant: isLinkedinIntro ? "linkedin_intro" : undefined,
          // Sent only when the operator actually chose. With 0-1 eligible links
          // the server resolves the piece's primary itself, so there is nothing
          // to send and nothing to get wrong.
          publish_result_id: mustPickLink ? chosenResultId : undefined,
          scheduled_at: scheduledAt ? new Date(scheduledAt).toISOString() : undefined,
        }),
      });
    },
    onSuccess: () => {
      onGenerated();
      onDone();
    },
  });

  return (
    <div className="settings-form" style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid var(--rule)" }}>
      {isLinkedinIntro && (
        <p className="panel-empty" style={{ margin: "0 0 8px", textAlign: "left" }}>
          Creates a concise LinkedIn post that introduces this piece and links back to it — not a
          follow-up angle.
        </p>
      )}
      {accounts.length === 0 && (
        <div className="panel-empty">
          {isLinkedinIntro
            ? "No LinkedIn account connected for this workspace."
            : "No connected accounts for this workspace."}
        </div>
      )}
      {accounts.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {accounts.map((account) => (
            <label key={account.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
              <input
                type="checkbox"
                checked={selectedIds.includes(account.id)}
                onChange={() => toggle(account.id)}
              />
              <span>
                {PLATFORM_LABEL[account.platform] ?? account.platform} — {account.display_name}
                {account.is_shared_to_me ? " (shared)" : ""}
              </span>
            </label>
          ))}
        </div>
      )}
      {/* FEAT-080 / S-351 — asked only when the piece genuinely has more than
          one live link. With 0-1 there is no question, and the spec is explicit
          that a single-target piece must never see this. */}
      {mustPickLink && (
        <div className="amp-link-pick">
          <label className="amp-link-pick-label" htmlFor="amp-link-pick">
            Which link should this reference?
          </label>
          <select
            id="amp-link-pick"
            className="settings-input"
            value={chosenResultId ?? ""}
            onChange={(e) => setPublishResultId(Number(e.target.value) || null)}
          >
            {/* Without this the select renders blank (selectedIndex -1) when no
                eligible result is primary — which is every already-published
                piece until the backfill runs. */}
            <option value="" disabled>
              Choose a link…
            </option>
            {eligible.map((r) => (
              <option key={r.id} value={r.id}>
                {r.target_name}
                {r.is_primary ? " (primary)" : ""} — {r.external_url}
              </option>
            ))}
          </select>
          {/* The redesign's house rule: wherever a control is disabled, say why. */}
          {chosenResultId == null && (
            <div className="pub-confirm-warn">
              Pick which link these posts should reference.
            </div>
          )}
        </div>
      )}
      <label className="settings-field" style={{ marginTop: 8 }}>
        <span className="settings-label">
          Scheduled at{" "}
          <span className="settings-label-hint">
            Optional. The post stays a draft either way — this only sets when it's due. Clear it to leave the time unset.
          </span>
        </span>
        <input
          type="datetime-local"
          className="settings-input"
          value={scheduledAt}
          onChange={(e) => setScheduledAt(e.target.value)}
        />
        {scheduledAt && scheduledAtTooSoon(scheduledAt) && (
          <span className="settings-field-hint">
            This time is inside the lead-time window — it will need changing before the post can be scheduled later.
          </span>
        )}
      </label>
      <textarea
        className="settings-input"
        placeholder="Optional angle — tell the model what spin to take this time"
        value={angle}
        onChange={(e) => setAngle(e.target.value)}
        rows={2}
        style={{ marginTop: 8, width: "100%", resize: "vertical" }}
      />
      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
        <button
          className="btn-dec btn-review"
          disabled={
            generate.isPending ||
            selectedIds.length === 0 ||
            // Until the results are loaded, `eligible` is [] and mustPickLink is
            // false — a fast click would post without a choice and silently fall
            // back to the primary, skipping a gate the spec calls required.
            resultsQ.isLoading ||
            (mustPickLink && chosenResultId == null)
          }
          onClick={() => generate.mutate()}
        >
          {generate.isPending ? "Generating…" : isLinkedinIntro ? "Generate intro post" : "Generate"}
        </button>
      </div>
      {generate.isError && (
        <span className="settings-field-error">
          {generate.error instanceof Error ? generate.error.message : "Generation failed."}
        </span>
      )}
    </div>
  );
}
